import { useState, useEffect } from 'react';

/**
 * Custom hook for debouncing values with configurable delay
 * @param value - The value to debounce
 * @param delay - Debounce delay in milliseconds (default: 400ms)
 * @returns The debounced value
 */
export const useDebounce = <T>(value: T, delay: number = 800): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    // Set up the timeout
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Cleanup function to clear the timeout if value changes before delay
    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
};

/**
 * Custom hook specifically for search functionality with optimizations
 * 
 * Benefits:
 * - Reduces API calls by waiting for user to stop typing
 * - Provides real-time feedback with isDebouncing state  
 * - Immediately clears search when input is empty
 * - Optimizes performance and reduces server load
 * 
 * @param searchTerm - The search term to debounce
 * @param delay - Debounce delay in milliseconds (default: 400ms)
 * @returns Object containing debounced search term and loading state
 */
export const useSearchDebounce = (searchTerm: string, delay: number = 400) => {
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);
  const [isDebouncing, setIsDebouncing] = useState(false);

  useEffect(() => {
    // Only debounce if there's actually a search term or if we're clearing it
    if (searchTerm.length === 0) {
      // Immediately clear when search is empty
      setDebouncedSearch('');
      setIsDebouncing(false);
      return;
    }

    setIsDebouncing(true);
    
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setIsDebouncing(false);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm, delay]);

  return {
    debouncedSearch,
    isDebouncing,
  };
};